import { useCallback, useEffect, useMemo, useState, type SyntheticEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import type { ProjectSummary } from '@indusketch/shared';
import { projectsApi } from '../../lib/api/endpoints.js';
import { userMessage } from '../../lib/errors.js';
import { useAuthStore } from '../auth/authStore.js';
import { ChangePasswordDialog } from '../auth/ChangePasswordDialog.js';
import {
  buildProjectTree,
  nextVariantName,
  type ProjectNode,
  type SortKey,
} from './projectTree.js';
import { AssetManager } from './AssetManager.js';
import { importProject, PROJECT_FILE_EXTENSION } from './projectFile.js';
import { Icon } from '../../ui/Icon.js';
import { MenuButton, type MenuItem } from '../../ui/Menu.js';
import { useI18n } from '../../i18n/i18n.js';
import { LanguageSwitcher } from '../../i18n/LanguageSwitcher.js';
import { VrProjectsHint } from '../editor/vr/VrProjectsHint.js';
import { ViewOnlyToggle } from '../inspect/ViewOnlyToggle.js';
import { useAutoRefresh } from '../admin/tableTools.js';
import { ActiveBadge, activeIn } from './ActiveBadge.js';

export function ProjectListPage() {
  const { m } = useI18n();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [name, setName] = useState('');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortKey>('updated');
  /** Root projects whose variants are shown; a search shows all matching variants. */
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  const reload = useCallback(async () => {
    try {
      setProjects((await projectsApi.list()).projects);
    } catch (err) {
      setError(userMessage(err));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    projectsApi
      .list()
      .then((res) => {
        if (!cancelled) setProjects(res.projects);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(userMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);
  // Background refresh for "being edited now"; a failed attempt simply waits for the next one.
  useAutoRefresh(() => {
    projectsApi
      .list()
      .then((res) => setProjects(res.projects))
      .catch(() => undefined);
  });

  const tree = useMemo(
    () => buildProjectTree(projects ?? [], query, sort),
    [projects, query, sort],
  );

  const guard = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await reload();
    } catch (err) {
      setError(userMessage(err));
    }
  };

  const toggle = (id: string, open?: boolean) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (open ?? !next.has(id)) next.add(id);
      else next.delete(id);
      return next;
    });

  const [importing, setImporting] = useState(false);
  const onImport = async (file: File) => {
    setError(null);
    setImporting(true);
    try {
      const id = await importProject(file);
      await navigate(`/projects/${id}`);
    } catch (err) {
      setError(userMessage(err));
      setImporting(false);
    }
  };

  const onCreate = async (e: SyntheticEvent) => {
    e.preventDefault();
    try {
      const res = await projectsApi.create({ name: name.trim() });
      await navigate(`/projects/${res.project.id}`);
    } catch (err) {
      setError(userMessage(err));
    }
  };

  const createVariant = (node: ProjectNode, source: ProjectSummary) =>
    guard(async () => {
      await projectsApi.duplicate(
        source.id,
        nextVariantName(node.project, node.variants, m.projects.variant),
      );
      toggle(node.project.id, true);
    });

  const searching = query.trim() !== '';

  return (
    <div className="page page-projects">
      <header className="page-header">
        <h1 className="page-title">
          {m.projects.title}
          {projects && projects.length > 0 && (
            <span className="count-badge">{projects.length}</span>
          )}
        </h1>
        <div className="page-actions">
          <LanguageSwitcher compact />
          <ViewOnlyToggle />
          {user?.role === 'ADMIN' && (
            <Link to="/admin" className="btn">
              <Icon name="settings" />
              {m.projects.admin}
            </Link>
          )}
          <MenuButton
            label={m.projects.userMenu}
            testId="user-menu"
            trigger={
              <>
                <Icon name="user" />
                <span className="user-name">{user?.displayName}</span>
                <Icon name="chevron-down" size={14} />
              </>
            }
            items={[
              { label: m.password.change, icon: 'lock', onSelect: () => setPasswordOpen(true) },
              { label: m.auth.logout, icon: 'arrow-left', onSelect: () => void logout() },
            ]}
          />
        </div>
      </header>
      {passwordOpen && <ChangePasswordDialog onClose={() => setPasswordOpen(false)} />}
      <VrProjectsHint />

      <form className="card create-row" onSubmit={(e) => void onCreate(e)}>
        <input
          className="input"
          placeholder={m.projects.newPlaceholder}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={200}
          aria-label={m.projects.projectName}
        />
        <button type="submit" className="btn btn-primary">
          <Icon name="plus" />
          {m.projects.create}
        </button>
        <label className={`btn${importing ? ' is-disabled' : ''}`} title={m.projects.importHint}>
          <Icon name="download" />
          {importing ? m.projects.importing : m.projects.import}
          <input
            type="file"
            accept={`${PROJECT_FILE_EXTENSION},.zip,.json`}
            hidden
            disabled={importing}
            data-testid="import-project"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) void onImport(file);
            }}
          />
        </label>
      </form>

      <section className="block-card projects-section" aria-label={m.projects.title}>
        <header className="block-head block-head-end">
          <div className="list-tools">
            <label className="search-field">
              <Icon name="search" />
              <input
                className="input"
                placeholder={m.projects.search}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label={m.projects.searchLabel}
              />
            </label>
            <select
              className="input"
              value={sort}
              onChange={(e) => setSort(e.target.value as SortKey)}
              aria-label={m.projects.sort}
            >
              <option value="updated">{m.projects.sortUpdated}</option>
              <option value="name">{m.projects.sortName}</option>
            </select>
          </div>
        </header>

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        {projects === null && !error && <p className="muted">{m.projects.loading}</p>}
        {projects?.length === 0 && (
          <div className="empty-state">
            <Icon name="hall" size={32} />
            <strong>{m.projects.emptyTitle}</strong>
            <p>{m.projects.emptyHint}</p>
          </div>
        )}
        {projects && projects.length > 0 && tree.length === 0 && (
          <p className="muted">{m.projects.noMatch}</p>
        )}

        <ul className="tile-grid project-grid" data-testid="project-list">
          {tree.map((node) => (
            <li key={node.project.id}>
              <ProjectTile
                node={node}
                open={searching || expanded.has(node.project.id)}
                onToggle={() => toggle(node.project.id)}
                onRename={(p, n) => guard(() => projectsApi.updateMeta(p.id, { name: n }))}
                onVariant={(p) => createVariant(node, p)}
                onDelete={(p) => guard(() => projectsApi.remove(p.id))}
                onLeave={(p) =>
                  guard(async () => {
                    if (user) await projectsApi.removeMember(p.id, user.id);
                  })
                }
              />
            </li>
          ))}
        </ul>
      </section>

      <AssetManager />

      <p className="disclaimer">{m.app.disclaimer}</p>
    </div>
  );
}

interface Actions {
  onRename: (project: ProjectSummary, name: string) => Promise<void>;
  onVariant: (project: ProjectSummary) => Promise<void>;
  onDelete: (project: ProjectSummary) => Promise<void>;
  onLeave: (project: ProjectSummary) => Promise<void>;
}

/** Name that can be edited in place; `editing` is controlled by the "⋯" menu. */
function useRename(p: ProjectSummary, onRename: Actions['onRename']) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(p.name);
  const start = () => {
    setDraft(p.name);
    setEditing(true);
  };
  const submit = async (e: SyntheticEvent) => {
    e.preventDefault();
    setEditing(false);
    if (draft.trim() && draft.trim() !== p.name) await onRename(p, draft.trim());
  };
  return { editing, draft, setDraft, start, submit, cancel: () => setEditing(false) };
}

function RenameForm({ rename }: { rename: ReturnType<typeof useRename> }) {
  const { m } = useI18n();
  return (
    <form className="inline-form rename-form" onSubmit={(e) => void rename.submit(e)}>
      <input
        className="input"
        value={rename.draft}
        onChange={(e) => rename.setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') rename.cancel();
        }}
        maxLength={200}
        aria-label={m.projects.newName}
        autoFocus
      />
      <button type="submit" className="btn btn-primary">
        {m.common.ok}
      </button>
    </form>
  );
}

function projectMenu(
  m: ReturnType<typeof useI18n>['m'],
  fmt: ReturnType<typeof useI18n>['fmt'],
  p: ProjectSummary,
  startRename: () => void,
  actions: Actions,
): MenuItem[] {
  const access = p.access ?? 'owner';
  const items: MenuItem[] = [];
  if (access !== 'viewer') {
    items.push(
      { label: m.common.renameAction, icon: 'pen', onSelect: startRename },
      {
        label: m.projects.createVariant,
        icon: 'copy-plus',
        onSelect: () => void actions.onVariant(p),
      },
    );
  }
  if (access === 'owner') {
    items.push({
      label: m.common.delete,
      icon: 'trash',
      danger: true,
      onSelect: () => {
        if (window.confirm(fmt(m.projects.confirmDelete, { name: p.name })))
          void actions.onDelete(p);
      },
    });
  } else {
    items.push({
      label: m.share.leave,
      icon: 'arrow-left',
      danger: true,
      onSelect: () => {
        if (window.confirm(fmt(m.share.confirmLeave, { name: p.name }))) void actions.onLeave(p);
      },
    });
  }
  return items;
}

function SharedBadge({ project: p }: { project: ProjectSummary }) {
  const { m, fmt } = useI18n();
  if (!p.access || p.access === 'owner' || !p.ownerName) return null;
  return (
    <span className="shared-badge" data-testid="shared-badge">
      <Icon name="users" size={13} />
      {fmt(m.share.sharedBy, { name: p.ownerName })} · {m.share.access[p.access]}
    </span>
  );
}

function ProjectTile({
  node,
  open,
  onToggle,
  ...actions
}: { node: ProjectNode; open: boolean; onToggle: () => void } & Actions) {
  const { m, fmt, plural, dateTime } = useI18n();
  const p = node.project;
  const rename = useRename(p, actions.onRename);
  const count = node.variants.length;
  const active = activeIn([p, ...node.variants]);
  return (
    <article
      className={`tile project-tile${active.length > 0 ? ' is-live' : ''}`}
      data-testid="project-row"
    >
      <ActiveBadge users={active} variant="overlay" />
      <Link
        to={`/projects/${p.id}`}
        className="tile-preview project-preview"
        tabIndex={-1}
        aria-hidden
      >
        {p.thumbnailUrl ? (
          <img src={p.thumbnailUrl} alt="" loading="lazy" />
        ) : (
          <Icon name="hall" size={40} className="tile-placeholder" />
        )}
      </Link>
      <div className="tile-body">
        {rename.editing ? (
          <RenameForm rename={rename} />
        ) : (
          <div className="tile-title-row">
            <Link to={`/projects/${p.id}`} className="tile-title tile-link">
              {p.name}
            </Link>
            <MenuButton
              label={fmt(m.projects.actions, { name: p.name })}
              items={projectMenu(m, fmt, p, rename.start, actions)}
            />
          </div>
        )}
        <span className="muted tile-meta">
          {fmt(m.projects.changed, { date: dateTime(p.updatedAt) })}
        </span>
        <SharedBadge project={p} />
        {count > 0 && (
          <button type="button" className="variant-toggle" aria-expanded={open} onClick={onToggle}>
            <Icon name="layers" size={14} />
            {plural(m.projects.variants, count)}
            <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} />
          </button>
        )}
      </div>
      {count > 0 && open && (
        <ul className="variant-list" aria-label={fmt(m.projects.variantsOf, { name: p.name })}>
          {node.variants.map((v) => (
            <li key={v.id}>
              <VariantRow project={v} {...actions} />
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

function VariantRow({ project: p, ...actions }: { project: ProjectSummary } & Actions) {
  const { m, fmt, dateTime } = useI18n();
  const rename = useRename(p, actions.onRename);
  if (rename.editing) return <RenameForm rename={rename} />;
  return (
    <div className="variant-row" data-testid="project-row">
      <Link to={`/projects/${p.id}`} className="variant-link">
        <span className="variant-name">{p.name}</span>
        <span className="muted tile-meta">{dateTime(p.updatedAt)}</span>
        <SharedBadge project={p} />
        <ActiveBadge users={p.activeUsers ?? []} variant="compact" />
      </Link>
      <MenuButton
        label={fmt(m.projects.actions, { name: p.name })}
        items={projectMenu(m, fmt, p, rename.start, actions)}
      />
    </div>
  );
}
